import { memo, useState } from 'react'

import { AudioStream, VideoStream } from '@nativewaves/nw-cloud-sdk/content'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { PickerDialogContent } from '@shared/components/PickerDialog'
import { audioStreamQueryKeys, videoStreamQueryKeys } from 'services/queryKeys'

export type Stream = VideoStream | AudioStream

export type StreamPickerDialogContentProps = {
  repositoryId?: string
  kind: 'video' | 'audio'
  /** Currently linked stream, highlighted in the list. */
  streamId?: string
  onSelect: (stream: Stream) => void
}

const StreamPickerDialogContentRaw = ({ repositoryId, kind, streamId, onSelect }: StreamPickerDialogContentProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const [queryValue, setQueryValue] = useState<string>()

  const isVideo = kind === 'video'

  const videoListQuery = useInfiniteQuery({
    ...videoStreamQueryKeys.list({ repositoryId: repositoryId!, query: queryValue, limit: 50 }),
    enabled: isVideo && !!repositoryId,
  })
  const audioListQuery = useInfiniteQuery({
    ...audioStreamQueryKeys.list({ repositoryId: repositoryId!, query: queryValue, limit: 50 }),
    enabled: !isVideo && !!repositoryId,
  })
  const listQuery = isVideo ? videoListQuery : audioListQuery
  const items = (listQuery.data?.pages.flatMap((page) => page.items) ?? []) as Stream[]

  return (
    <PickerDialogContent<Stream>
      items={items}
      listQuery={listQuery}
      onQueryChange={setQueryValue}
      searchPlaceholder={t('domain:Workspace.Event.ConfigEditor.selectStream')}
      emptyText={t('domain:Workspace.Event.ConfigEditor.noStreams')}
      getKey={(stream) => stream.id}
      getPrimary={(stream) => stream.name || `${t('id')} (${stream.id})`}
      getSecondary={(stream) => stream.id}
      isSelected={(stream) => stream.id === streamId}
      onSelect={onSelect}
    />
  )
}

export const StreamPickerDialogContent = memo(StreamPickerDialogContentRaw)
